import { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  useToast,
  useColorModeValue,
  Center,
  Spinner,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  List,
  ListItem,
  ListIcon,
  Icon,
  Image,
  Heading,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import {
  MdPlayCircle,
  MdDescription,
  MdQuiz,
  MdCheckCircle,
  MdSchool,
  MdAccessTime,
  MdPerson,
  MdArrowBack,
} from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getCourseById,
  enrollInCourse,
  unenrollFromCourse,
  Course,
  Module
} from '../services/university-service';
import { useRouteGuard } from '@bemap/utils';
import {
  PageHeading,
  AnimatedCard,
  containerVariants,
  cardVariants,
  Panel
} from '@bemap/ui';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

interface CourseWithModules extends Course {
  modules?: Module[];
}

export default function CourseEnrollment() {
  const [course, setCourse] = useState<CourseWithModules | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const { user } = useRouteGuard();

  const cardBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
  }, [courseId]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      const response = await getCourseById(parseInt(courseId!));

      if (response.statusCode === 200) {
        const courseData = response.message as CourseWithModules;
        setCourse(courseData);
      } else {
        toast({
          title: 'Erro ao carregar curso',
          description: response.message || 'Falha ao carregar os detalhes do curso',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Erro ao carregar curso',
        description: error.message || 'Falha ao carregar os detalhes do curso',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!course || !user) return;

    try {
      setEnrolling(true);
      const response = await enrollInCourse(course.id, user.id);

      if ([200, 201].includes(response.statusCode)) {
        setIsEnrolled(true);
        toast({
          title: 'Inscrição realizada!',
          description: `Você foi inscrito no curso "${course.title}"`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Erro na inscrição',
          description: response.message || 'Falha ao realizar a inscrição',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Erro na inscrição',
        description: error.message || 'Falha ao realizar a inscrição',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setEnrolling(false);
    }
  };

  const handleUnenroll = async () => {
    if (!course || !user) return;

    try {
      setEnrolling(true);
      const response = await unenrollFromCourse(course.id, user.id);

      if (response.statusCode === 200) {
        setIsEnrolled(false);
        toast({
          title: 'Inscrição cancelada',
          description: `Sua inscrição no curso "${course.title}" foi cancelada`,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Erro ao cancelar inscrição',
          description: response.message || 'Falha ao cancelar a inscrição',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Erro ao cancelar inscrição',
        description: error.message || 'Falha ao cancelar a inscrição',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setEnrolling(false);
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'green';
      case 'intermediate': return 'orange';
      case 'advanced': return 'red';
      default: return 'gray';
    }
  };

  const getLevelText = (level: string) => {
    switch (level) {
      case 'beginner': return 'Iniciante';
      case 'intermediate': return 'Intermediário';
      case 'advanced': return 'Avançado';
      default: return 'Desconhecido';
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  const getContentIcon = (type: string) => {
    switch (type) {
      case 'video': return MdPlayCircle;
      case 'document': return MdDescription;
      case 'quiz': return MdQuiz;
      default: return MdDescription;
    }
  };

  const getContentColor = (type: string) => {
    switch (type) {
      case 'video': return 'blue.500';
      case 'document': return 'green.500';
      case 'quiz': return 'purple.500';
      default: return 'gray.500';
    }
  };

  const calculateTotalDuration = () => {
    if (!course?.modules) return 0;
    return course.modules.reduce((total, module) => total + module.durationMinutes, 0);
  };

  const calculateTotalContents = () => {
    if (!course?.modules) return 0;
    return course.modules.reduce((total, module) => {
      return total + (module.contents?.length || 0);
    }, 0);
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!course) {
    return (
      <Center h="400px">
        <Text>Curso não encontrado</Text>
      </Center>
    );
  }

  return (
    <MotionBox
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      as={VStack}
      gap={4}
      align="stretch"
      alignItems="stretch"
    >
      <PageHeading
        title={course.title}
        actions={[
          {
            label: 'Voltar',
            onClick: () => navigate(-1),
            icon: <MdArrowBack />,
            variant: 'outline',
          },
          {
            label: isEnrolled ? 'Cancelar Inscrição' : 'Inscrever-se',
            onClick: isEnrolled ? handleUnenroll : handleEnroll,
            icon: isEnrolled ? <MdCheckCircle /> : <MdSchool />,
            colorScheme: isEnrolled ? 'red' : 'blue',
            isLoading: enrolling,
          }
        ]}
      />

      {/* Course Overview */}
      <AnimatedCard bg={cardBg} variants={cardVariants}>
        <VStack align="stretch" spacing={4}>
          {course.thumbnailUrl && (
            <Image
              src={course.thumbnailUrl}
              alt={course.title}
              borderRadius="lg"
              height="300px"
              objectFit="cover"
              fallbackSrc="https://via.placeholder.com/600x300?text=Sem+Imagem"
            />
          )}

          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between" wrap="wrap">
              <Badge colorScheme={getLevelColor(course.level)} size="lg">
                {getLevelText(course.level)}
              </Badge>
              <HStack spacing={4}>
                <HStack>
                  <Icon as={MdAccessTime} color="gray.500" />
                  <Text fontSize="sm" color="gray.500">
                    {formatDuration(calculateTotalDuration())}
                  </Text>
                </HStack>
                <HStack>
                  <Icon as={MdPerson} color="gray.500" />
                  <Text fontSize="sm" color="gray.500">
                    {course.instructor}
                  </Text>
                </HStack>
              </HStack>
            </HStack>

            <Text fontSize="lg" color={textColor} lineHeight="1.6">
              {course.description}
            </Text>

            <Divider />

            {/* Course Stats */}
            <Flex justify="space-around" wrap="wrap" gap={4}>
              <Stat>
                <StatLabel color="gray.500">Módulos</StatLabel>
                <StatNumber color="blue.600">{course.modules?.length || 0}</StatNumber>
                <StatHelpText>Total de módulos</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel color="gray.500">Conteúdos</StatLabel>
                <StatNumber color="green.600">{calculateTotalContents()}</StatNumber>
                <StatHelpText>Vídeos, documentos e quizzes</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel color="gray.500">Duração</StatLabel>
                <StatNumber color="purple.600">{formatDuration(calculateTotalDuration())}</StatNumber>
                <StatHelpText>Tempo total do curso</StatHelpText>
              </Stat>
            </Flex>
          </VStack>
        </VStack>
      </AnimatedCard>

      {/* Course Modules */}
      <Panel heading="Conteúdo do Curso">
        {course.modules && course.modules.length > 0 ? (
          <Accordion allowMultiple>
            {course.modules
              .sort((a, b) => a.order - b.order)
              .map((module) => (
                <AccordionItem key={module.id} border="1px" borderColor={borderColor} borderRadius="md" mb={2}>
                  <AccordionButton py={4} _hover={{ bg: 'gray.50' }}>
                    <Box flex="1" textAlign="left">
                      <HStack justify="space-between">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold" color={textColor}>
                            Módulo {module.order}: {module.title}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            {module.contents?.length || 0} conteúdos • {formatDuration(module.durationMinutes)}
                          </Text>
                        </VStack>
                        <Badge colorScheme={module.completed ? 'green' : 'gray'} size="sm">
                          {module.completed ? 'Concluído' : 'Pendente'}
                        </Badge>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <VStack align="stretch" spacing={3}>
                      <Text fontSize="sm" color="gray.600">
                        {module.description}
                      </Text>

                      {module.contents && module.contents.length > 0 && (
                        <List spacing={2}>
                          {module.contents
                            .sort((a, b) => a.order - b.order)
                            .map((content) => (
                              <ListItem key={content.id}>
                                <HStack spacing={3}>
                                  <ListIcon
                                    as={getContentIcon(content.type)}
                                    color={getContentColor(content.type)}
                                  />
                                  <VStack align="start" spacing={0} flex="1">
                                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                                      {content.title}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      {content.duration} • {content.type}
                                    </Text>
                                  </VStack>
                                  {content.completed && (
                                    <Icon as={MdCheckCircle} color="green.500" />
                                  )}
                                </HStack>
                              </ListItem>
                            ))}
                        </List>
                      )}
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              ))}
          </Accordion>
        ) : (
          <Text color="gray.500" textAlign="center" py={8}>
            Este curso ainda não possui módulos disponíveis.
          </Text>
        )}
      </Panel>

      {/* Enrollment CTA */}
      {!isEnrolled && (
        <AnimatedCard bg={cardBg} variants={cardVariants}>
          <VStack spacing={4} textAlign="center">
            <Icon as={MdSchool} boxSize={12} color="blue.500" />
            <VStack spacing={2}>
              <Heading size="md" color={textColor}>
                Pronto para começar?
              </Heading>
              <Text color="gray.600">
                Inscreva-se neste curso para começar sua jornada de aprendizado
              </Text>
            </VStack>
            <Button
              colorScheme="blue"
              size="lg"
              leftIcon={<MdSchool />}
              onClick={handleEnroll}
              isLoading={enrolling}
              loadingText="Inscrevendo..."
            >
              Inscrever-se no Curso
            </Button>
          </VStack>
        </AnimatedCard>
      )}
    </MotionBox>
  );
} 